<template>
    <div>
        <!-- Step 1 -->
        <transition name="fade" mode="out-in">
            <div v-if="step == 1">
                <h4 class="mb-3">Calcolo Retta</h4>
                <form @submit.prevent="submitStep1">
                    <div class="p-4 p-md-5 bg-white border rounded">
                        <!-- Nome -->
                        <div class="row mb-2">
                            <label for="firstName" class="col-md-4 col-form-label text-md-end">Nome</label>
                            <div class="col-12 col-md-8">
                                <input type="text" class="form-control" id="firstName" v-model="form.first_name" :readonly="first_name" required>
                            </div>
                        </div>
                        <!-- Cognome -->
                        <div class="row mb-2">
                            <label for="lastName" class="col-md-4 col-form-label text-md-end">Cognome</label>
                            <div class="col-12 col-md-8">
                                <input type="text" class="form-control" id="lastName" v-model="form.last_name" :readonly="last_name" required>
                            </div>
                        </div>
                        <!-- Codice Fiscale -->
                        <div class="row mb-2">
                            <label for="fiscalCode" class="col-md-4 col-form-label text-md-end">Codice Fiscale</label>
                            <div class="col-12 col-md-8">
                                <input type="text" class="form-control" :class="{ 'is-invalid': !validation.fiscal_code }" id="fiscalCode" v-model="form.fiscal_code" v-uppercase :readonly="fiscal_code" required>
                                <div class="invalid-feedback" v-if="!validation.fiscal_code">
                                    Codice Fiscale non valido o non corrispondente al nome e/o al cognome.
                                </div>
                            </div>
                        </div>

                        <hr/>

                        <!-- Corso di Laurea -->
                        <div class="row mb-2" v-if="!course">
                            <label for="course" class="col-md-4 col-form-label text-md-end">Corso di Laurea</label>
                            <div class="col-12 col-md-8">
                                <select
                                    class="form-select"
                                    aria-label="Corso di Laurea"
                                    id="course"
                                    v-model="form.course"
                                    required
                                    @change="changeCourse"
                                >
                                    <option :value="null" disabled>Seleziona...</option>
                                    <option v-for="(course, index) in helpers.courses" :key="index" :value="course">
                                        {{ course.name }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <!-- Tipo di Ingresso -->
                        <div class="row mb-2">
                            <label for="entrance" class="col-md-4 col-form-label text-md-end">Tipo di Ingresso</label>
                            <div class="col-12 col-md-8">
                                <select class="form-select" aria-label="Tipo di Ingresso" id="entrance" v-model="form.entrance" required>
                                    <option :value="null" disabled>Seleziona...</option>
                                    <option v-for="(entrance, index) in helpers.entrances" :key="index" :value="entrance">
                                        {{ entrance.label }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <!-- Doppia Laurea -->
                        <div class="row mb-2">
                            <label for="doppia_laurea" class="col-md-4 col-form-label text-md-end">Doppia Laurea</label>
                            <div class="col-12 col-md-8">
                                <select class="form-select" aria-label="Doppia Laurea" id="doppia_laurea" v-model="form.doppia_laurea" required>
                                    <option :value="null" disabled>Seleziona...</option>
                                    <option :value="0">Iscrizione Unica</option>
                                    <option :value="1">Già iscritto presso questo Ateneo</option>
                                    <option :value="2">Già iscritto presso altro Ateneo</option>
                                </select>
                                <div v-if="form.doppia_laurea == 2" class="mt-2">
                                    <v-select
                                        :options="helpers.universities"
                                        v-model="form.doppia_laurea_ateneo"
                                        :clearable="false"
                                        :required="form.doppia_laurea == 2"
                                        placeholder="Seleziona Ateneo"
                                    />
                                </div>
                            </div>
                        </div>
                        <!-- Anno Accademico -->
                        <div class="row mb-2">
                            <label for="aa" class="col-md-4 col-form-label text-md-end">Anno Accademico</label>
                            <div class="col-12 col-md-8">
                                <select
                                    class="form-select"
                                    aria-label="Anno Accademico"
                                    id="aa"
                                    v-model="form.aa"
                                    required
                                    :disabled="!form.entrance"
                                    @change="changeAA"
                                >
                                    <option :value="null" disabled>Seleziona...</option>
                                    <option v-for="(aa, index) in helpers.aa" :key="index" :value="aa">
                                        {{ aa }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <!-- Piano di Studi -->
                        <div class="row mb-2">
                            <label for="careers" class="col-md-4 col-form-label text-md-end">Piano di Studi</label>
                            <div class="col-12 col-md-8">
                                <select class="form-select" aria-label="Piano di Studi" id="careers" v-model="form.pds" :disabled="!form.aa" required @change="getConventions()">
                                    <option :value="null" disabled>Seleziona...</option>
                                    <option v-for="(career, index) in helpers.careers" :key="index" :value="career.id">
                                        {{ career.label }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <hr/>

                        <!-- ECP -->
                        <div class="row mb-2">
                            <label for="ecp" class="col-md-4 col-form-label text-md-end">{{ university.ecp_text }}</label>
                            <div class="col-12 col-md-8">
                                <v-select
                                    :options="helpers.ecps"
                                    v-model="form.ecp"
                                    :clearable="false"
                                    :disabled="!!ecp || !form.aa || ecp_code"
                                    @update:modelValue="getConventions">
                                </v-select>
                            </div>
                        </div>
                        <!-- Agevolazione -->
                        <div class="row mb-2">
                            <label for="convention" class="col-md-4 col-form-label text-md-end">Agevolazione</label>
                            <div class="col-12 col-md-8">
                                <v-select
                                    :options="helpers.conventions"
                                    v-model="form.convention"
                                    :clearable="false"
                                    :disabled="!form.aa || !!convention"
                                    @update:modelValue="getPrograms(); getCompanies()">
                                </v-select>
                                <div v-if="form.convention && form.convention.description" class="mt-2 border rounded p-2 lh-1 small bg-light">
                                    <small class="text-muted">{{ form.convention.description }}</small>
                                    <div v-if="form.convention.label.includes('MULTIVERSITY')" class="mt-2">
                                        <div class="row mb-2">
                                            <div class="col-12">
                                                <div class="form-check small text-muted">
                                                    <input class="form-check-input" type="checkbox" v-model="form.multiversity_card_selected" id="multiversity_card_selected" value="1" required>
                                                    <label class="form-check-label" for="multiversity_card_selected">
                                                        <strong>Obbligatorio</strong> 
                                                        Dichiaro di richiedere l'adesione al Programma Membership Multiversity Card e di aver preso visione 
                                                        del <a href="https://assets.ctfassets.net/5bcqzxwt09xw/6J8VztVSiEwFhVTT4BFoPS/685054db0fa5aedd73b84069eb332edd/Regolamento_Multiversity_Card.pdf" target="_blank" class="link-secondary">Regolamento - Termini e Condizioni</a>.
                                                        Prendo atto che la presente richiesta non comporta l'immediata conclusione del 
                                                        contratto di adesione al Programma Membership Multiversity Card, che si perfezionerà successivamente 
                                                        al completamento della procedura di immatricolazione universitaria, mediante specifica 
                                                        accettazione e sottoscrizione del relativo Regolamento, nonché previo pagamento della quota 
                                                        di adesione prevista.
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row mb-2">
                                            <div class="col-12">
                                                <div class="form-check small text-muted">
                                                    <input class="form-check-input" type="checkbox" v-model="form.multiversity_card_privacy" id="multiversity_card_privacy" value="1" required>
                                                    <label class="form-check-label" for="multiversity_card_privacy">
                                                        <strong>Obbligatorio</strong> 
                                                        Dichiaro di aver letto e compreso l'<a :href="university.multiversity_card" target="_blank" class="link-secondary">informativa</a> sul trattamento dei dati personali nell'ambito del 
                                                        Programma Membership Multiversity Card.
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-12">
                                                <div class="form-check small text-muted">
                                                    <input class="form-check-input" type="checkbox" v-model="form.multiversity_card_marketing" id="multiversity_card_marketing" value="1">
                                                    <label class="form-check-label" for="multiversity_card_marketing">
                                                        Acconsento al trattamento dei dati personali per la finalità di marketing di Multiversity S.p.A. 
                                                        - con strumenti tradizionali (es. chiamate tramite operatore) e automatizzati (es. e-mail, 
                                                        instant messaging e popup) e permettendo anche l'ottimizzazione dei risultati delle campagne mediante pixel 
                                                        - meglio descritta al paragrafo 4 (finalità n. 4 della Sezione B) 
                                                        dell'<a :href="university.multiversity_card" target="_blank" class="link-secondary">informativa</a> 
                                                        sul trattamento dei dati personali nell'ambito del Programma Membership Multiversity Card
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Codice Fiscale Dipendente -->
                        <div class="row mb-2" v-if="form.convention && form.convention.label == 'FAMILIARI DIPENDENTI'">
                            <label for="fiscal_code" class="col-md-4 col-form-label text-md-end">Codice Fiscale Dipendente</label>
                            <div class="col-12 col-md-8">
                                <input
                                    type="text"
                                    class="form-control"
                                    id="fiscal_code"
                                    required
                                    v-model="form.fiscal_code_dipendente"
                                >
                            </div>
                        </div>
                        <!-- Enti/Aziende -->
                        <div class="row mb-2" v-if="helpers.companies.length > 0">
                            <label for="convention" class="col-md-4 col-form-label text-md-end">
                                {{ form.convention && form.convention.label == 'CATEGORIE TUTELATE' ? 'Categoria' : 'Enti/Aziende' }}
                            </label>
                            <div class="col-12 col-md-8">
                                <v-select
                                    :options="helpers.companies"
                                    v-model="form.company"
                                    :clearable="false">
                                </v-select>
                            </div>
                        </div>
                        <!-- Programma -->
                        <div class="row mb-2">
                            <label for="program" class="col-md-4 col-form-label text-md-end">Formula</label>
                            <div class="col-12 col-md-8">
                                <select
                                    class="form-select"
                                    aria-label="Formula"
                                    id="program"
                                    v-model="form.program"
                                    @change="()=>{getInstallments(); getLabels(); getImport()}"
                                    :disabled="!form.aa || !!program"
                                    required>
                                        <option :value="null" disabled>Seleziona...</option>
                                        <option v-for="program in helpers.programs" :key="program.value" :value="{ id: program.id, label: program.label, promo_code: program.promo_code }">
                                            {{ program.label }}
                                        </option>
                                </select>
                            </div>
                        </div>
                        <!-- Rate -->
                        <div class="row mb-2">
                            <label for="installment" class="col-md-4 col-form-label text-md-end">Rate</label>
                            <div class="col-12 col-md-8">
                                <select
                                    class="form-select"
                                    aria-label="Rate"
                                    id="installment"
                                    v-model="form.installments"
                                    @change="getImport"
                                    :disabled="!form.aa"
                                    required
                                >
                                    <option :value="null" disabled>Seleziona...</option>
                                    <option v-for="installment in helpers.installments" :key="installment.value"
                                            :value="{ id: installment.id, label: installment.label }">
                                        {{ installment.label }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <!-- Programma Formativo -->
                        <div class="row mb-2" v-if="form.aa == '2025/2026'">
                            <label for="label" class="col-md-4 col-form-label text-md-end">Programma Formativo</label>
                            <div class="col-12 col-md-8">
                                <select
                                    class="form-select"
                                    aria-label="Programma Formativo"
                                    id="label"
                                    v-model="form.label"
                                    :disabled="!form.program || !label"
                                    @change="getImport"
                                >
                                    <option value="">Nessuno</option>
                                    <option v-for="(label, index) in helpers.labels" :key="index" :value="label">
                                        {{ label }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        
                        <hr/>

                        <!-- Privacy -->
                        <div class="row mb-2">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small lh-sm">
                                    <input class="form-check-input" type="checkbox" v-model="form.privacy" id="privacy" value="1" required>
                                    <label class="form-check-label text-secondary" for="privacy">
                                        <strong>Obbligatorio</strong> Dichiaro di aver letto e compreso l'informativa sul trattamento dei dati personali
                                        dell'<strong>{{ university.name }}</strong> (<a :href="university.privacy_student" title="Informativa" target="_blank" class="link-secondary">Informativa</a>)
                                    </label>
                                </div>
                            </div>
                        </div>
                        <!-- Marketing -->
                        <div class="row mb-2">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small lh-sm">
                                    <input class="form-check-input" type="checkbox" v-model="form.marketing" id="marketing" value="1">
                                    <label class="form-check-label text-secondary" for="marketing">
                                        Acconsento al trattamento dei dati personali per la finalità di marketing dell'<strong>{{ university.name }}</strong>
                                        - con strumenti tradizionali (chiamate tramite operatore) e automatizzati (e-mail, instant messaging e popup) e permettendo anche l'ottimizzazione dei risultati delle campagne mediante pixel - 
                                        meglio descritta al paragrafo 4 (finalità n. 10) dell'informativa sul trattamento dei dati personali 
                                        (<a :href="university.privacy_student" title="Informativa" target="_blank" class="link-secondary">Informativa</a>)
                                    </label>
                                </div>
                            </div>
                        </div>
                        <!-- Statistiche -->
                        <div class="row mb-2">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small lh-sm">
                                    <input class="form-check-input" type="checkbox" v-model="form.statistics" id="statistics" value="1">
                                    <label class="form-check-label text-secondary" for="statistics">
                                        Acconsento al trattamento dei dati personali per la finalità di analisi statistiche dell'<strong>{{ university.name }}</strong> 
                                        meglio descritta al paragrafo 4 (finalità n. 11) dell’informativa sul trattamento dei dati personali  
                                        (<a :href="university.privacy_student" title="Informativa" target="_blank" class="link-secondary">Informativa</a>)
                                    </label>
                                </div>
                            </div>
                        </div>
                        <!-- Alumni -->
                        <div class="row mb-2">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small lh-sm">
                                    <input class="form-check-input" type="checkbox" v-model="form.alumni" id="alumni" value="1">
                                    <label class="form-check-label text-secondary" for="alumni">
                                        Acconsento al trattamento dei dati per gestire la mia adesione all'Associazione Alumni dell'<strong>{{ university.name }}</strong>, 
                                        meglio descritta al paragrafo 4 (finalità n. 15) dell’informativa sul trattamento dei dati personali
                                        (<a :href="university.privacy_student" title="Informativa" target="_blank" class="link-secondary">Informativa</a>)
                                    </label>
                                </div>
                            </div>
                        </div>
                        <!-- Progetti -->
                        <div class="row">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small lh-sm">
                                    <input class="form-check-input" type="checkbox" v-model="form.progetti" id="progetti" value="1">
                                    <label class="form-check-label text-secondary" for="progetti">
                                        Acconsento al trattamento dei dati per ricevere inviti a partecipare a questionari nell’ambito di progetti di ricerca
                                        dell'<strong>{{ university.name }}</strong>, come meglio descritto al paragrafo 4 (finalità n. 17) dell’informativa sul trattamento dei dati
                                        personali
                                        (<a :href="university.privacy_student" title="Informativa" target="_blank" class="link-secondary">Informativa</a>)
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Validazione + Email -->
                        <div class="alert alert-danger mt-3" role="alert" v-if="!validation.enrol">
                            <h5><i class="fas fa-exclamation-triangle me-2"></i>Impossibile proseguire</h5>
                            <p class="mb-0">{{ validation.message }}</p>
                            <button type="button" class="btn btn-outline-danger mt-3" v-if="validation.sendEmail && !temp.sendedEmail" @click="sendEmailTemp">
                                <i class="fas fa-circle-notch fa-spin" v-if="temp.sendingEmail"></i>
                                <i class="far fa-envelope me-2" v-else></i>
                                Reinvia e-mail per l'accesso all'area personale
                            </button>
                        </div>
                        <div class="alert alert-success mt-3" v-if="temp.sendedEmail">
                            <h5><i class="fas fa-check-circle me-2"></i>E-Mail inviata</h5>
                            <p class="mb-0">Controlla la tua casella di posta elettronica.</p>
                        </div>

                    </div>
                    <div class="pt-3 text-end">
                        <button class="btn btn-success" type="submit" :disabled="helpers.sending">
                            Prosegui
                        </button>
                    </div>
                </form>
            </div>
        </transition>

        <!-- Step 2 -->
        <transition name="fade" mode="out-in">
            <div v-if="step == 2">
                <h4 class="mb-2">Dati Anagrafici</h4>
                <form @submit.prevent="submitStep2">
                    <div class="p-4 p-md-5 bg-white border rounded">
                        <!-- Sesso -->
                        <div class="row mb-2">
                            <label for="gender" class="col-md-4 col-form-label text-md-end">Sesso</label>
                            <div class="col-12 col-md-8">
                                <select class="form-select" aria-label="Sesso" id="gender" v-model="form.gender" required>
                                    <option value="" disabled>Seleziona</option>
                                    <option value="M">Maschio</option>
                                    <option value="F">Femmina</option>
                                </select>
                            </div>
                        </div>
                        <!-- Data di Nascita -->
                        <div class="row mb-2">
                        <label for="birthDate" class="col-md-4 col-form-label text-md-end">Data di Nascita</label>
                        <div class="col-12 col-md-8">
                            <VueDatePicker 
                                v-model="form.birth_date"
                                model-type="format"
                                format="yyyy-MM-dd"
                                valueType="format"
                                class="w-100"
                                locale="it"
                                placeholder="aaaa-mm-gg"
                                default-panel="year"
                                :enable-time-picker="false"
                                :input-attr="{ required: true }">
                            </VueDatePicker>
                            <span v-if="validation.birth_date" class="small m-0 text-danger">La data di nascita è un campo obbligatorio</span>
                        </div>
                        </div>
                        <!-- Nazione di Nascita -->
                        <div class="row mb-2">
                        <label for="birthNation" class="col-md-4 col-form-label text-md-end">Nazione di Nascita</label>
                        <div class="col-12 col-md-8">
                            <v-select
                                :options="helpers.birth_nations"
                                v-model="form.birth_nation"
                                :clearable="false">
                            </v-select>
                        </div>
                        </div>
                        <!-- Provincia di Nascita -->
                        <div class="row mb-2" v-if="form.birth_nation.id == 'IT'">
                        <label for="birthProvince" class="col-md-4 col-form-label text-md-end">Provincia di Nascita</label>
                        <div class="col-12 col-md-8">
                            <v-select
                                :options="helpers.birth_provinces"
                                :value="form.birth_province"
                                v-model="form.birth_province"
                                :clearable="false"
                                @update:modelValue="changeBirthProvince"
                            >
                            <template #search="{attributes, events}">
                                <input class="vs__search" :required="!form.birth_province" v-bind="attributes" v-on="events"/>
                            </template>
                            </v-select>
                        </div>
                        </div>
                        <div class="row mb-2">
                        <label for="birthCity" class="col-md-4 col-form-label text-md-end">Comune di Nascita</label>
                        <div class="col-12 col-md-8">
                            <v-select :options="helpers.birth_cities" v-model="form.birth_city" :clearable="false"
                                    v-if="form.birth_nation.id == 'IT'">
                            <template #search="{attributes, events}">
                                <input class="vs__search" :required="!form.birth_city" v-bind="attributes" v-on="events"/>
                            </template>
                            </v-select>
                            <input type="text" class="form-control" v-model="form.birth_city" id="birthCity" v-else required>
                        </div>
                        </div>
                    </div>
                    <div class="pt-3 d-flex justify-content-between">
                        <button class="btn btn-secondary" type="button" @click="goBackStep">
                            <i class="fas fa-angle-left me-2"></i>Indietro
                        </button>
                        <button class="btn btn-success" type="submit">
                            Prosegui<i class="fas fa-angle-right ms-2"></i>
                        </button>
                    </div>
                </form>
            </div>
        </transition>

        <!-- Step 3 -->
        <div v-if="step == 3">
        <h4 class="mb-2">Recapiti</h4>
        <form @submit.prevent="submitStep3">
            <div class="p-4 p-md-5 bg-white border rounded">
            <div class="row mb-2">
                <label for="email" class="col-md-4 col-form-label text-md-end">E-Mail</label>
                <div class="col-12 col-md-8">
                <input type="email" class="form-control" id="email" v-model="form.email" required>
                </div>
            </div>
            <div class="row mb-2">
                <label for="email2" class="col-md-4 col-form-label text-md-end">Ripeti E-Mail</label>
                <div class="col-12 col-md-8">
                <input type="email" class="form-control" :class="{ 'is-invalid': !validation.email }" id="email2"
                        v-model="form.email_2" required>
                <div class="invalid-feedback" v-if="!validation.email">
                    La mail non corrisponde.
                </div>
                </div>
            </div>
            <div class="row mb-2">
                <label for="telephone" class="col-md-4 col-form-label text-md-end">Recapito Telefonico</label>
                <div class="col-12 col-md-8">
                <input type="text" class="form-control" id="telephone" v-model="form.telephone" required>
                </div>
            </div>
            <div class="row mb-2">
                <label for="addressNation" class="col-md-4 col-form-label text-md-end">Nazione di Residenza</label>
                <div class="col-12 col-md-8">
                <v-select :options="helpers.address_nations" v-model="form.address_nation" :clearable="false"></v-select>
                </div>
            </div>
            <div class="row mb-2" v-if="form.address_nation.id == 'IT'">
                <label for="addressProvince" class="col-md-4 col-form-label text-md-end">Provincia di Residenza</label>
                <div class="col-12 col-md-8">
                <v-select :options="helpers.address_provinces" v-model="form.address_province" :clearable="false"
                            @update:modelValue="changeAddressProvince">
                    <template #search="{attributes, events}">
                    <input class="vs__search" :required="!form.address_province" v-bind="attributes" v-on="events"/>
                    </template>
                </v-select>
                </div>
            </div>
            <div class="row mb-2">
                <label for="addressCity" class="col-md-4 col-form-label text-md-end">Comune di Residenza</label>
                <div class="col-12 col-md-8">
                <v-select :options="helpers.address_cities" v-model="form.address_city" :clearable="false"
                            v-if="form.address_nation.id == 'IT'">
                    <template #search="{attributes, events}">
                    <input class="vs__search" :required="!form.address_city" v-bind="attributes" v-on="events"/>
                    </template>
                </v-select>
                <input type="text" class="form-control" v-model="form.address_city" id="addressCity" v-else required>
                </div>
            </div>
            <div class="row mb-2">
                <label for="address" class="col-md-4 col-form-label text-md-end">Indirizzo di Residenza</label>
                <div class="col-12 col-md-6">
                <input type="text" class="form-control" v-model="form.address" id="address" required>
                </div>
                <div class="col-12 col-md-2">
                <input type="text" class="form-control" v-model="form.address_number" placeholder="Civico" required>
                </div>
            </div>
            <div class="row mb-2">
                <label for="cap" class="col-md-4 col-form-label text-md-end">CAP</label>
                <div class="col-12 col-md-3">
                    <input type="text" class="form-control" v-model="form.cap" id="cap" @input="checkCAP" required :class="{'is-invalid': !validation.cap}">
                    <div class="invalid-feedback" v-if="!validation.cap">Formato non valido</div>
                </div>
            </div>
            <div class="alert alert-danger mt-3" role="alert" v-if="!validation.store">
                <h5><i class="fas fa-exclamation-triangle me-2"></i>Impossibile proseguire</h5>
                <p class="mb-0">Si è verificato un errore, contatta il supporto tecnico!</p>
            </div>
            </div>
            <div class="pt-3 d-flex justify-content-between">
            <button class="btn btn-secondary" @click="goBackStep">
                <i class="fas fa-angle-left me-2"></i>Indietro
            </button>
            <button class="btn btn-success" type="submit" :disabled="helpers.sending || !validation.cap">
                Prosegui
                <i class="fas fa-circle-notch fa-spin ms-2" v-if="helpers.sending"></i>
                <i class="fas fa-angle-right ms-2" v-else></i>
            </button>
            </div>
        </form>
        </div>

        <!-- Step 4 -->
        <div v-if="step == 4">
        <h4 class="mb-2">Area personale</h4>
        <div class="p-4 p-md-5 bg-white border rounded">
            <p>Gentile <strong>{{ form.first_name }} {{ form.last_name }}</strong>,</p>
            <p>
            la sua richiesta è andata a buon fine!<br/>
            Per poter completare la sua iscrizione le chiediamo di caricare i documenti richiesti
            nella sua pagina personale.
            </p>
            <a :href="access_link" target="_blank" class="btn btn-success">
            <i class="fas fa-sign-in-alt me-2"></i>Accedi all'area personale
            </a>
        </div>
        </div>
    </div>
</template>

<script>
    import * as services from '../../services';

    export default {
        props: {
            step: {
                type: Number
            },
            course: {
                type: Object,
                required: false
            }
        },
        data() {
            return {
                form: {
                    course: null,
                    first_name: null,
                    last_name: null,
                    fiscal_code: null,
                    entrance: null,
                    doppia_laurea: null,
                    doppia_laurea_ateneo: null,
                    aa: null,
                    pds: null,
                    label: '',
                    ecp: null,
                    convention: null,
                    program: null,
                    installments: null,
                    mgm_code: null,
                    prospect: null,
                    prospect_next: null,
                    privacy: 1,
                    marketing: 0,
                    statistics: 0,
                    gender: '',
                    birth_date: null,
                    birth_nation: {'label': 'ITALIA', 'id': 'IT'},
                    birth_province: null,
                    birth_city: null,
                    address: null,
                    address_number: null,
                    address_nation: {'label': 'ITALIA', 'id': 'IT'},
                    address_province: null,
                    address_city: null,
                    cap: null,
                    promo_code: null,
                    supplement_program_id: null,
                    insurance: false,
                    insurance_firstname: null,
                    insurance_lastname: null,
                    insurance_fiscal_code: null,
                    insurance_birthdate: null,
                    insurance_birthcity: null,
                    company: null,
                    multiversity_card_marketing: 0,
                    multiversity_card_selected: 0,
                    multiversity_card_privacy: 0,
                    fiscal_code_dipendente: null,
                    progetti: 0,
                    alumni: 0
                },
                helpers: {
                    courses: [],
                    entrances: [],
                    universities: [],
                    aa: [],
                    careers: [],
                    labels: [],
                    ecps: [],
                    conventions: [],
                    programs: [],
                    installments: [],
                    birth_nations: [],
                    birth_provinces: [],
                    birth_cities: [],
                    address_nations: [],
                    address_provinces: [],
                    address_cities: [],
                    sending: false,
                    companies: []
                },
                validation: {
                    email: true,
                    fiscal_code: true,
                    enrol: true,
                    message: null,
                    sendEmail: false,
                    store: true,
                    mgm_code: null,
                    mgm_code_message: null,
                    cap: true,
                    birth_date: false
                },
                temp: {
                    sendingEmail: false,
                    sendedEmail: false
                },
                access_link: null
            }
        },
        computed: {
            assicurazione() {
                return this.$store.getters['settings/assicurazione']
            },
            user_id() {
                return this.$store.getters['settings/user_id']
            },
            first_name() {
                return this.$store.getters['settings/first_name']
            },
            last_name() {
                return this.$store.getters['settings/last_name']
            },
            fiscal_code() {
                return this.$store.getters['settings/fiscal_code']
            },
            university() {
                return this.$store.getters['settings/from']
            },
            token() {
                return this.$store.getters['settings/token']
            },
            ecp() {
                return this.$store.getters['settings/ecp']
            },
            ecp_code() {
                return this.$store.getters['settings/ecp_code']
            },
            convention() {
                return this.$store.getters['settings/convention']
            },
            program() {
                return this.$store.getters['settings/program']
            },
            label() {
                return this.$store.getters['settings/label']
            },
            tracking() {
                return this.$store.getters['settings/tracking']
            },
            provider() {
                return this.$store.getters['settings/provider']
            },
            gads_conversion() {
                return this.$store.getters['settings/gads_conversion']
            },
            floodlight_conversion() {
                return this.$store.getters['settings/floodlight_conversion']
            },
            criteo_conversion() {
                return this.$store.getters['settings/criteo_conversion']
            },
            crm() {
                return this.$store.getters['settings/crm']
            },
            privacy() {
                return this.$store.getters['settings/site_url'] + '/privacy'
            },
            campaign() {
                return this.$store.getters['settings/campaign']
            },
            source() {
                return this.$store.getters['settings/source']
            },
            mp_tot() {
                return this.$store.getters['summary/mp_tot']
            }
        },
        methods: {
            async getCourses() {
                const data = await services.ecp.post('api/get/graduate/courses')
                const excludedCodes = ["02GIURI", "040", "015", "003"];
                this.helpers.courses = data.data
                    .filter(el => !excludedCodes.includes(el.code))
                    .map(el => {
                        return {
                            name: el.name,
                            code: el.code
                        };
                    });
            },
            async changeCourse() {
                this.$store.dispatch('summary/setCourse', this.form.course.name)
                this.form.entrance = null
                this.form.pds = null
                this.form.aa = null
                await this.getEntrance()
                await this.getUniversities()
                await this.getAcademicYear()
                await this.getCareers()
                await this.getConventions()
            },
            async getEntrance() {
                const data = await services.ecp.post('api/get/graduate/tipo/ingresso', {
                    courseCode: this.form.course.code
                });
                this.helpers.entrances = data.data.map(el => {
                    return {
                    label: el.DESCRIZIONE,
                    id: el.ID_TIPO_INGRESSO
                    }
                })
            },
            async getUniversities() {
                const data = await services.ecp.get('api/atenei')
                this.helpers.universities = data.data.map(({ID_UNIVERSITA, nome}) => ({id: ID_UNIVERSITA, label: nome}));
            },
            async getAcademicYear() {
                const data = await services.ecp.post('api/get/graduate/academicYear/currents', {
                    course_code: this.form.course.code
                })
                let aa = data.data
                this.helpers.aa = aa
            },
            async getLabels()
            {
                const labels = await services.ecp.post('api/get/graduate/labels', {
                    course_code: this.form.course.code,
                    academicYear: this.form.aa,
                    program_id:this.form.program.id,
                    convention_id:this.form.convention.real_id
                })
                this.helpers.labels = labels.data
            },
            async getCareers() {
                this.helpers.careers = null
                this.helpers.labels = null
                const careers = await services.ecp.post('api/get/graduate/careers', {
                    course_code: this.form.course.code,
                    academicYear: this.form.aa
                })
                this.helpers.careers = careers.data.map(el => {
                    return {
                        label: el.name,
                        id: el.id
                    }
                })
                await this.getImport()
            },
            async changeAA() {
                await this.getCareers()
                await this.getConventions()
                await this.getPrograms()
            },
            async getEcp() {
                const data = await services.ecp.get('api/get/ecp-active-no-master')
                this.helpers.ecps = data.data.map(el => {
                    return {
                        'label': el.multiorienta_code.toUpperCase(),
                        'id': el.user_id,
                        'codice': el.codice.toUpperCase(),
                        'multiorienta_id': el.multiorienta_id 
                    }
                })
                if (!this.ecp && !this.ecp_code) {
                    this.form.ecp = this.helpers.ecps.find(el => el.label == 'DIREZIONALE')
                } else if (this.ecp_code) {
                    let find_ecp = this.helpers.ecps.find(el => el.label == this.ecp_code)
                    if (typeof find_ecp !== 'undefined') {
                        this.form.ecp = find_ecp
                    } else {
                        this.form.ecp = this.helpers.ecps.find(el => el.label == 'DIREZIONALE')
                    }
                } else {
                    this.form.ecp = this.helpers.ecps.find(el => el.id == this.ecp)
                }
            },
            async getConventions() 
            {
                let data
                data = await services.ecp.post('api/get/graduate/conventions', {
                    aa: this.form.aa
                })

                let descriptions = {
                    'STANDARD': null,
                    'ENTI CONVENZIONATI': 'Agevolazione riservata agli iscritti/dipendenti di un ente convenzionato con l\'Ateneo (es. sindacati, categorie di rappresentanza).',
                    'MULTIVERSITY CARD': 'Agevolazione riservata agli aderenti al Programma Membership Multiversity Card, erogato da Multiversity S.p.A., che consente l\'accesso a sconti e ulteriori benefici extra-universitari. L\'adesione al Programma prevede il pagamento di una quota annuale di € 50 (IVA inclusa). Il mantenimento della tariffa agevolata per gli anni accademici successivi è subordinato alla permanenza di una membership valida ed efficace. Pertanto, in caso di mancato rinnovo o cessazione della membership, l\'utente perderà il diritto a beneficiare dell\'agevolazione economica per l\'anno accademico di riferimento.',
                    'MULTIVERSITY CARD - START': 'Agevolazione riservata agli studenti che si immatricolano al primo anno di un corso di laurea triennale o magistrale a ciclo unico, anche con riconoscimento di crediti formativi universitari (CFU). L\'agevolazione è riservata agli aderenti al Programma Membership Multiversity Card, erogato da Multiversity S.p.A., che consente l\'accesso a sconti e ulteriori benefici extra-universitari. L\'adesione al Programma prevede il pagamento di una quota annuale di € 50 (IVA inclusa). Il mantenimento della tariffa agevolata per gli anni accademici successivi è subordinato alla permanenza di una membership valida ed efficace. Pertanto, in caso di mancato rinnovo o cessazione della membership, l\'utente perderà il diritto a beneficiare dell\'agevolazione economica per l’anno accademico di riferimento.',
                    'FUTURO SICURO': 'Agevolazione riservata ai giovani studenti d\'età compresa tra i 17 e i 21 anni che si iscrivono al primo anno di un corso di laurea triennale o magistrale a ciclo unico (anche con riconoscimento di CFU).',
                    'FORZE ARMATE E FORZE DELL\'ORDINE': 'Agevolazione riservata al personale in servizio militare permanente all’interno di uno dei Corpi delle Forze Armate e Forze dell’Ordine, ai prossimi congiunti (coniuge e figli) del personale attualmente in servizio, nonché ai conviventi e figli regolati dalla L. 20/5/2016, n. 76 (Unioni Civili). La medesima agevolazione è estesa anche al personale in congedo (personale in pensione).',
                    'PUBBLICA AMMINISTRAZIONE': 'Agevolazione dedicata ai dipendenti della Pubblica Amministrazione e ai prossimi congiunti (coniuge e figli).',
                    'CATEGORIE TUTELATE': 'Agevolazione riservata agli studenti con Disturbi Specifici di Apprendimento, a donne in gravidanza e a neo genitori con figli dai 0 ai 24 mesi.',
                    'AZIENDE CONVENZIONATE': 'Agevolazione riservata ai dipendenti di un\'azienda convenzionata con l\'Ateneo.',
                    'DOPPIA LAUREA': 'Agevolazione riservata agli studenti che scelgono di frequentare due corsi di laurea in contemporanea (sia all\'interno dell\'Ateneo che combinando l\'iscrizione con un altro istituto universitario).',
                    'INVALIDITA\' > 45%': 'Agevolazione riservata agli studenti con disabilità certificata maggiore del 45%.',
                    'INTESA SANPAOLO - SOCI ALI': 'Agevolazione riservata ai soci ordinari, beneficiari familiari e altri beneficiari in possesso della tessera associativa Associazione Lavoratori Intesa Sanpaolo (ALI), nonché ai loro familiari di primo grado, conviventi e figli regolati dalla L. 20 maggio 2016, n. 76.',
                    'CAMERE DI COMMERCIO': 'Agevolazione riservata ai dipendenti, figli e coniugi dei dipendenti delle Camere di Commercio e delle loro articolazioni funzionali ivi compresi e componenti dei Consigli Camerali.',
                    'FAMILIARI DIPENDENTI': 'Agevolazione riservata ai familiari dei dipendenti del Gruppo Multiversity.'
                }
                
                this.helpers.conventions = data.data
                    .filter(el => el.name != 'DIPENDENTI')
                    .map(el => {
                        return {
                            label: el.name.toUpperCase().replace('26/27 - ', ''),
                            real_id: el.id,
                            id: el.id,
                            description: descriptions[el.name.toUpperCase().replace('26/27 - ', '')] ? descriptions[el.name.toUpperCase().replace('26/27 - ', '')] : null,
                        }
                })

                const courseCodes = [
                    "001",
                    "04SCUMA",
                    "011",
                    "012",
                    "013",
                    "014",
                    "02GIURI",
                    "016",
                    "03GIURI",
                    "018",
                    "00SCUMA",
                    "020",
                    "02SCUMA",
                    "022",
                    "024",
                    "026",
                    "003",
                    "07GIURI",
                    "036",
                    "004",
                    "004",
                    "040",
                    "041",
                    "06SCUMA",
                    "01GIURI",
                    "007",
                    "008",
                    "008",
                    "009",
                    "009",
                    "053",
                    "00GIURI"
                ];
                
                if (this.form.course && this.form.course.code == '013') { // L-13 Scienze Biologiche
                    this.helpers.conventions = this.helpers.conventions.filter(el => el.label === 'SCIENZE BIOLOGICHE');
                } else if (this.form.course && courseCodes.includes(this.form.course.code)) { // Triennali
                    let multiversity_card = this.helpers.conventions.find(el => el.label == 'MULTIVERSITY CARD' || el.label == 'MULTIVERSITY')
                    this.helpers.conventions.push({
                        label: 'MULTIVERSITY CARD - START',
                        real_id: multiversity_card.real_id,
                        id: 999999,
                        description: descriptions['MULTIVERSITY CARD - START'] ? descriptions['MULTIVERSITY CARD - START'] : null
                    })
                    if (this.form.course && this.form.course.code == '016') {
                        if (this.form.pds) {
                            if (this.form.pds == 1262) { // Calcio
                                this.helpers.conventions = this.helpers.conventions.filter(el => el.label.includes('FOOTBALL MANAGEMENT'));
                            } else {
                                this.helpers.conventions = this.helpers.conventions.filter(el => 
                                    el.label != 'SCIENZE BIOLOGICHE' &&
                                    !el.label.includes('FOOTBALL MANAGEMENT')
                                );
                            }
                        } else {
                            this.helpers.conventions = this.helpers.conventions.filter(el => el.label != 'SCIENZE BIOLOGICHE');
                        }
                    } else {
                        this.helpers.conventions = this.helpers.conventions.filter(
                            el => 
                                el.label != 'SCIENZE BIOLOGICHE' && 
                                !el.label.includes('FOOTBALL MANAGEMENT') && 
                                el.label != 'PROMO LAUREATI 24/25'
                        );
                    }
                } else {
                    this.helpers.conventions = this.helpers.conventions.filter(el => el.label != 'FUTURO SICURO' && el.label != 'SCIENZE BIOLOGICHE' && !el.label.includes('FOOTBALL MANAGEMENT'));
                }

                this.helpers.conventions.sort((a, b) => a.label.localeCompare(b.label));

                if (!this.convention) {
                    if (this.form.course && this.form.course.code == '013') {
                        this.form.convention = this.helpers.conventions.find(el => el.label == 'SCIENZE BIOLOGICHE')
                    } else if (this.form.course && this.form.course.code == '016' && this.form.pds && this.form.pds == 1262) { // Calcio
                        this.form.convention = this.helpers.conventions.find(el => el.label == 'FOOTBALL MANAGEMENT - FORMULA COLLEGE')
                    } else {
                        this.form.convention = this.helpers.conventions.find(el => el.label == 'STANDARD' || el.label == 'STANDARD-UTP' || el.label == 'STANDARD-UTSR')
                    }
                } else {
                    this.form.convention = this.helpers.conventions.find(el => el.id == this.convention)
                }

                if (this.form.course && this.form.course.code) {
                    this.getPrograms()
                    this.getCompanies()
                }
            },
            async getCompanies() 
            {
                this.form.company = null
                const data = await services.ecp.post('api/get/graduate/companies', {
                    convention_id: this.form.convention.real_id
                })
                this.helpers.companies = data.data.map(el => {
                    return {
                        label: el.name.toUpperCase(),
                        id: el.id
                    }
                })
            },  
            async getPrograms() 
            {
                this.form.program = null
                this.form.prospect = null
                this.form.prospect_next = null
                this.form.installments = null
                this.helpers.installments = []

                const data = await services.ecp.post('api/get/graduate/programs', {
                    convention_id: this.form.convention.real_id,
                    label: this.form.label,
                    academicYear: this.form.aa,
                    category_code: this.form.course ? this.form.course.code : null
                })

                this.helpers.programs = data.data.map(el => {
                    return {
                        label: el.name.toUpperCase(),
                        id: el.id,
                        promo_code: !!el.promo_code,
                    }
                })

                this.helpers.programs.sort((a, b) => a.label.localeCompare(b.label));

                if (this.program) {
                    this.form.program = this.helpers.programs.find(el => el.id == this.program)
                }
            },
            async getInstallments() 
            {
                this.form.prospect = null
                this.form.prospect_next = null
                const promo_code = this.$getPromoCode(this.form, this.university.crm)

                // applicazione del codice promo
                if (promo_code != '') {
                    const dataPromo = await services.ecp.post('api/check-promo-code', {
                        promo_code: promo_code,
                        fiscal_code: this.form.fiscal_code ? this.form.fiscal_code : 'RSSMRA80A01F205X',
                        course_code: this.form.course.code,
                        convention_id: this.form.convention.real_id,
                        program_id: this.form.program.id,
                        academicYear: this.form.aa,
                    });
                    const elPromo = dataPromo.data
                    this.form.supplement_program_id = elPromo.supplement_program_id
                }
                this.form.promo_code = promo_code

                // rate
                this.helpers.installments = []
                this.form.installments = null
                const data = await services.ecp.post('api/get/graduate/installments', {
                    convention_id: this.form.convention.real_id,
                    program_id: this.form.program.id
                })
                
                this.helpers.installments = data.data.map(el => {
                    return {
                        label: el.num_rate,
                        id: el.id
                    }
                })
            },
            async getImport() 
            {
                this.form.prospect = null
                this.form.prospect_next = null
                const aa = this.form.aa
                const label = this.form.label
                const program = this.form.program
                const installments = this.form.installments
                const course_code = this.form.course.code
                const convention_id = this.form.convention.real_id
                const promo_code = this.form.promo_code
                const fiscal_code = this.form.fiscal_code ? this.form.fiscal_code : 'RSSMRA80A01F205X'

                if (aa && program && installments) {
                    const data = await services.ecp.post('api/get/graduate/prospectus', {
                        academicYear: aa,
                        label: label,
                        program: program.id,
                        installments: installments.id,
                        course_code,
                        convention_id,
                        promo_code,
                        fiscal_code,
                    })
                    this.form.prospect = data.data
                    const prospectus_next = await services.ecp.post('api/get/graduate/sub-prospectus', {
                        academicYear: aa,
                        label: label,
                        program: program.id,
                        installments: installments.id,
                        course_code,
                        convention_id,
                        promo_code,
                        fiscal_code,
                    })
                    this.form.prospect_next = prospectus_next.data;
                }
            },
            async getNations() {
                const data = await services.ecp.post('api/get/nations')
                return data.data.map(el => {
                    return {
                        'label': el.NAZIONE,
                        'id': el.ID_NAZIONALITA
                    }
                })
            },
            async getProvinces() {
                const data = await services.ecp.post('api/get/province')
                return data.data.map(el => {
                    return {
                        'label': el.PROVINCIA,
                        'id': el.SIGLA
                    }
                })
            },
            async changeBirthProvince() {
                const province_id = this.form.birth_province.id
                this.form.birth_city = null
                this.helpers.birth_cities = await this.getCities(province_id)
            },
            async changeAddressProvince() {
                const province_id = this.form.address_province.id
                this.form.address_city = null
                this.helpers.address_cities = await this.getCities(province_id)
            },
            async getCities(province_id) {
                const data = await services.ecp.post('api/get/cities', {
                    province: province_id
                })
                return data.data.map(el => {
                    return {
                        'label': el.COMUNE,
                        'id': el.COD_ISTAT
                    }
                })
            },
            async checkMgmCode() {
                try {
                    const { data } = await services.ecp.post('api/check-mgm-code', {
                        mgm_code: this.form.mgm_code,
                        fiscal_code: this.form.fiscal_code
                    });
                    this.validation.mgm_code = 'success';
                    this.form.ecp = this.helpers.ecps.find(el => el.id == data.ecp_id);
                    this.$store.dispatch('settings/setEcp', data.ecp_id);
                } catch (_) {
                    console.log(_);
                    this.validation.mgm_code = 'fail';
                    this.validation.mgm_code_message = 'Il codice inserito non è valido';
                }
            },
            checkCAP() {
                if (this.form.cap == '') {
                    this.validation.cap = true
                    return
                }
                let pattern = new RegExp('^[0-9]{5,5}$');
                if (!this.form.cap.match(pattern)) {
                    this.validation.cap = false
                } else {
                    this.validation.cap = true
                }
            },
            goBackStep() {
                const step = this.step - 1
                this.$emit('update', step)
            },
            async submitStep1() {
                this.helpers.sending = true
                try {
                    const check_enrol = await services.ecp.post('api/get/graduate/fiscal/code/check', {
                        courseCode: this.form.course.code,
                        fiscalCode: this.form.fiscal_code,
                        academicYear: this.form.aa
                    })
                    this.validation.enrol = true
                } catch (error) {
                    this.validation.enrol = false
                    const response = error.response
                    switch (response.status) {
                        case 409:
                            this.validation.message = response.data
                            this.validation.sendEmail = true
                            break
                        case 403:
                            this.validation.message = response.data
                            break
                        case 410:
                            this.validation.message = 'Studente proveniente da MASTER LM. Abbiamo inviato una e-mail allo studente contentente l\'indirizzo per il caricamento dei documenti necessari all\'iscrizione'
                            break
                        default:
                            this.validation.message = response.data
                            break
                    }
                }
                this.helpers.sending = false
                this.validation.email = this.form.email == this.form.email_2
                if (this.validation.fiscal_code && this.validation.enrol && this.validation.email) {
                    const step = this.step + 1
                    this.$emit('update', step)
                }
            },
            async sendEmailTemp() {
                this.temp.sendingEmail = true
                try {
                    const data = await services.ecp.post('api/send/graduate/enrolment/online/mail', {
                        courseCode: this.form.course.code,
                        fiscalCode: this.form.fiscal_code
                    })
                    this.temp.sendingEmail = false
                    this.temp.sendedEmail = true
                } catch (error) {
                    this.temp.sendingEmail = false
                    console.log(error)
                }
            },
            submitStep2() {
                this.validation.birth_date = this.form.birth_date == '' || this.form.birth_date === null ? true : false
                if (this.validation.birth_date) return
                const step = this.step + 1
                this.$emit('update', step)
            },
            async submitStep3() {
                this.helpers.sending = true
                const form = this.form
                const birth_date = form.birth_date.split('-')
                let request = {}
                let crm_id = 0
                let sendToEnrol = {}
                let storeInCrm = true

                // crm
                if (this.crm === true) {
                    try {
                        const crmFields = {
                            from: this.university.crm,
                            first_name: form.first_name,
                            last_name: form.last_name,
                            email: form.email,
                            telephone: form.telephone,
                            fiscal_code: form.fiscal_code,
                            birth_date: form.birth_date,
                            gender: form.gender,
                            province: form.address_nation.id == 'IT' ? form.address_province.label : form.address_province,
                            city: form.address_nation.id == 'IT' ? form.address_city.label : form.address_city,
                            type: 4,
                            category: 2,
                            subject: form.course.name,
                            provider: this.provider,
                            campaign: this.campaign ? this.campaign : this.$crm.get('campaign', '(not set)'),
                            privacy: 1,
                            marketing: form.marketing,
                            statistics: form.statistics,
                            ga_id: this.$crm.get('_ga'),
                            gclid: this.$crm.get('gclid'),
                            hubspotutk: this.$crm.get('hubspotutk'),
                            device: this.$crm.getDeviceInfo(),
                            pages: this.$crm.getPages(),
                            time: this.$crm.getTime(),
                            code: form.course.code,
                            enrol_type: form.entrance.label,
                            classform: form.label === '' ? 0 : 1,
                            ecp: form.ecp.codice,
                            multiorienta_id: form.ecp.multiorienta_id,
                            convention: form.convention.label,
                            program: form.program.label,
                            import: form.prospect.program.import
                        }
                        const sendToCrm = await services.crm.post('crm/public/request', crmFields)
                        
                        request = sendToCrm.data.data.request
                        crm_id = sendToCrm.data.data.request.id
                        storeInCrm = true 
                    } catch (error) {
                        storeInCrm = false
                        console.log(error)
                    }
                }

                // segreteria
                if (storeInCrm) {
                    try {
                        const enrolFields = {
                            courseCode: form.course.code,
                            ecp: form.ecp.id,
                            academicYear: form.aa,
                            tipologia: form.entrance.id,
                            doppia_laurea_ateneo: form.doppia_laurea_ateneo?.label,
                            doppia_laurea_id: form.doppia_laurea_ateneo?.id,
                            pds: form.pds,
                            label: form.label,
                            convention: form.convention.real_id,
                            program: form.program.id,
                            installments: form.installments.id,
                            firstname: form.first_name,
                            lastname: form.last_name,
                            fiscalCode: form.fiscal_code,
                            email: form.email,
                            email_confirmation: form.email_2,
                            phone: form.telephone,
                            gender: form.gender,
                            birthdate: birth_date[2] + '/' + birth_date[1] + '/' + birth_date[0],
                            birthnation: form.birth_nation.id,
                            birthprovince: form.birth_nation.id == 'IT' ? form.birth_province.id : null,
                            birthcity: form.birth_nation.id == 'IT' ? form.birth_city.id : form.birth_city,
                            address: form.address,
                            address_n: form.address_number,
                            nation: form.address_nation.id,
                            province: form.address_nation.id == 'IT' ? form.address_province.id : null,
                            city: form.address_nation.id == 'IT' ? form.address_city.id : form.address_city,
                            cap: form.cap,
                            crm_id: crm_id,
                            supplement_program_id: form.supplement_program_id,
                            promo_code: form.promo_code,
                            insurance_firstname: form.insurance_firstname,
                            insurance_lastname: form.insurance_lastname,
                            referent: form.fiscal_code_dipendente,
                            insurance_birthdate: form.insurance_birthdate,
                            insurance_birthcity: form.insurance_birthcity,
                            mgm_code: this.validation.mgm_code === 'success' ? form.mgm_code : undefined,
                            user_id: this.user_id,
                            company_id: form.company ? form.company.id : null,
                            multiorienta_id: form.ecp.multiorienta_id,
                            source: this.source,
                            multiversity_card_selected: form.multiversity_card_selected,
                            multiversity_marketing_consent: form.multiversity_card_marketing
                        }
                        sendToEnrol = await services.ecp.post('api/send/graduate/enrolment/online/web', enrolFields)
                        this.validation.store = true
                        this.access_link = 'https://' + this.university.api + '.multiversity.click/main/online/graduate_registration.php?online_token=' + sendToEnrol.data.token
                    } catch (error) {
                        this.validation.store = false
                        console.log(error)
                    }
                } else {
                    this.validation.store = false
                }
                // tracking
                if (this.tracking === true && storeInCrm && this.validation.store) {
                    console.log(this.university.crm);
                    if (this.university.crm == 'MERCATORUM'  || this.university.crm == 'SANRAFFAELE') {
                        this.$gtm.trackOnlineRegistration(
                            'CDL', //registration_type
                            this.university.crm, //from
                            form.course.name, //course_name
                            form.course.code, //transaction_id - course_code chiedere
                            crm_id, //contact_id
                            Number(this.$store.getters['summary/mp_tot'] || 0), //value importo
                            form.gender, //user_gender
                            form.birth_date, //user_birth_date
                            form.ecp.codice, //ecp_code
                            form.convention.label,//entrance_type
                            form.email, //user_data.email
                            form.telephone, //user_data.phone_number
                            form.first_name, //user_data.first_name
                            form.last_name,//user_data.last_name
                            form.address_nation.id == 'IT' ? form.address_city.label : form.address_city, //user_data.address.city
                            ); 
                    } else {
                        if (this.$CookieConsent.acceptedCategory('analytics')) {
                            this.$crm.trackGAEnrol('CDL', form.prospect.program.import)
                        }
                        if (this.$CookieConsent.acceptedCategory('marketing')) {
                            if (this.gads_conversion) {
                                this.$crm.trackGoogleAdsEnrol(this.gads_conversion, 'CDL', form.prospect.program.import, form.course.name.substring(0, 100))
                            }
                            if (this.floodlight_conversion) {
                                this.$crm.trackFloodlightEnrol(
                                    this.floodlight_conversion, 
                                    crm_id, 
                                    'CDL', 
                                    form.course.name.substring(0, 100), 
                                    form.prospect.program.import, 
                                    form.gender, 
                                    form.birth_date, 
                                    form.ecp.label, 
                                    form.entrance, 
                                    this.$crm.get('_ga'), 
                                    request.contact_id
                                )
                            }
                            this.$crm.trackFacebookEnrol(crm_id)
                            this.$crm.trackBingAdsEnrol('CDL', form.prospect.program.import)
                            //this.$crm.trackEnhancedConversion(request.contact.email, request.contact.telephone)
                            this.$crm.trackCdpLead(request.contact_id, request.subject, 4)
                        }
                        this.$crm.resetTracking();
                    }
                }
                
                this.helpers.sending = false
                if (this.validation.store) {
                    const step = this.step + 1
                    this.$emit('update', step)
                }
            }
        },
        async mounted() {
            if (!this.course) {
                await this.getCourses()
            } else {
                this.form.course = this.course
                this.$store.dispatch('summary/setCourse', this.course.name)
                await this.getAcademicYear()
                await this.getEntrance()
            }
            await this.getUniversities()
            await this.getEcp()
            await this.getConventions()
            const nations = await this.getNations()
            const provinces = await this.getProvinces()
            this.helpers.birth_nations = nations
            this.helpers.address_nations = nations
            this.helpers.birth_provinces = provinces
            this.helpers.address_provinces = provinces
            if (this.first_name) this.form.first_name = this.first_name
            if (this.last_name) this.form.last_name = this.last_name
            if (this.fiscal_code) this.form.fiscal_code = this.fiscal_code
        },
        watch: {
            'form.aa' (val) {
                if (val) this.$store.dispatch('summary/setAA', val)
            },
            'form.doppia_laurea'(val) {
                this.form.doppia_laurea_ateneo = null;
            },
            'form.insurance'(val) {
                if (!val) {
                    this.form.insurance_firstname = null;
                    this.form.insurance_lastname = null;
                    this.form.insurance_birthdate = null;
                    this.form.insurance_birthcity = null;
                    this.form.insurance_fiscal_code = null;
                }
            },
            'form.ecp'(val) {
                this.$store.dispatch('summary/setEcp', val.label)
            },
            'form.convention'(val) {
                this.$store.dispatch('summary/setConvention', val.label)
            },
            'form.program'(val) {
                const program = val ? val.label : val
                this.$store.dispatch('summary/setProgram', program)
            },
            'form.prospect'(val) {
                if (val) {
                    this.$store.dispatch('summary/setImport', val.program.import)
                    this.$store.dispatch('summary/setInstallments', val.prospectus)
                } else {
                    this.$store.dispatch('summary/setImport', null)
                    this.$store.dispatch('summary/setInstallments', null)
                }
            },
            'form.prospect_next'(val) {
                if (val) {
                    this.$store.dispatch('summary/setInstallmentsNext', val)
                } else {
                    this.$store.dispatch('summary/setInstallmentsNext', null)
                }
            },
            'form.course'(val, old) {
                if (val && val.code !== old?.code) {
                    this.form.promo_code = null
                }
            },
            'form.installments' (val) {
                if (val) {
                    this.$store.dispatch('summary/setRate', val.label)
                } else {
                    this.$store.dispatch('summary/setRate', null)
                }
            },
            'form.promo_code' (val) {
                if (val) {
                    this.$store.dispatch('summary/setPromoCode', val)
                } else {
                    this.$store.dispatch('summary/setPromoCode', null)
                }
        }
        }
    }
</script>